import {
  DEFAULT_IVA_RATE,
  roundMoney,
  toExpenseWriteData,
  validateExpense,
  type ExpenseErrors,
  type ExpenseWriteData,
} from "./expenseRules";
import type {
  Expense,
  ExpenseFileKind,
  ExpensePaidBy,
  ExpensePaymentMethod,
} from "./expenseTypes";

/**
 * Almikia Epic EG (TASK-044): pure helpers of the Gastos screens (list filters,
 * header total, form values <-> write payload). Framework-free so they unit-test
 * without rendering anything.
 */

/** Expense file kinds with the `expenses` column each one is stored in. */
export const EXPENSE_FILE_COLUMNS = {
  receipt: "receipt_path",
  cfdi_pdf: "cfdi_pdf_path",
  cfdi_xml: "cfdi_xml_path",
} as const satisfies Record<ExpenseFileKind, string>;

/** `accept` attribute of each file picker (the bucket enforces the same MIME types). */
export const EXPENSE_FILE_ACCEPT: Record<ExpenseFileKind, string> = {
  receipt: "application/pdf,image/jpeg,image/png,image/webp",
  cfdi_pdf: "application/pdf",
  cfdi_xml: "application/xml,text/xml",
};

/**
 * First and last day of a `YYYY-MM` month, or null when the value is not a month.
 */
export function monthToRange(
  month: string | null | undefined,
): { from: string; to: string } | null {
  const match = /^(\d{4})-(0[1-9]|1[0-2])$/.exec(month ?? "");
  if (!match) return null;
  const year = Number(match[1]);
  const monthNumber = Number(match[2]);
  // Day 0 of the next month is the last day of this one.
  const lastDay = new Date(Date.UTC(year, monthNumber, 0)).getUTCDate();
  return {
    from: `${match[1]}-${match[2]}-01`,
    to: `${match[1]}-${match[2]}-${String(lastDay).padStart(2, "0")}`,
  };
}

/** The `YYYY-MM` month a stored date range filter selects ('' when none). */
export function rangeToMonth(from: unknown): string {
  return typeof from === "string" ? from.slice(0, 7) : "";
}

/** `YYYY-MM-DD` -> `DD/MM/YYYY`, without going through a timezone-shifting `Date`. */
export function formatExpenseDate(date: string | null | undefined): string {
  const match = /^(\d{4})-(\d{2})-(\d{2})/.exec(date ?? "");
  return match ? `${match[3]}/${match[2]}/${match[1]}` : "—";
}

/** Today as `YYYY-MM-DD` in the user's local calendar. */
export function todayIsoDate(now: Date = new Date()): string {
  const month = String(now.getMonth() + 1).padStart(2, "0");
  const day = String(now.getDate()).padStart(2, "0");
  return `${now.getFullYear()}-${month}-${day}`;
}

/** Total of the given expenses, cancelled ones excluded; rounded once at the end of each add. */
export function sumActiveExpenses(
  rows: Pick<Expense, "amount_mxn" | "cancelled_at">[],
): number {
  return rows.reduce(
    (total, row) =>
      row.cancelled_at == null ? roundMoney(total + row.amount_mxn) : total,
    0,
  );
}

/** What the form edits; every input is a string except the switches. */
export type ExpenseFormValues = {
  expense_date: string;
  concept: string;
  category_id: string;
  supplier: string;
  amount: string;
  has_iva: boolean;
  iva_rate: number;
  is_invoiced: boolean;
  cfdi_uuid: string;
  cfdi_pdf_path: string;
  cfdi_xml_path: string;
  paid_by: ExpensePaidBy;
  paid_by_other: string;
  payment_method: ExpensePaymentMethod;
  project_id: string;
  notes: string;
  receipt_path: string;
};

/** Initial form values: the expense being edited, or sensible defaults for a new one. */
export function toExpenseFormValues(
  expense?: Partial<Expense>,
  today: string = todayIsoDate(),
): ExpenseFormValues {
  return {
    expense_date: expense?.expense_date ?? today,
    concept: expense?.concept ?? "",
    category_id:
      expense?.category_id == null ? "" : String(expense.category_id),
    supplier: expense?.supplier ?? "",
    amount: expense?.amount_mxn == null ? "" : String(expense.amount_mxn),
    has_iva: expense?.has_iva ?? true,
    iva_rate: expense?.iva_rate ?? DEFAULT_IVA_RATE,
    is_invoiced: expense?.is_invoiced ?? false,
    cfdi_uuid: expense?.cfdi_uuid ?? "",
    cfdi_pdf_path: expense?.cfdi_pdf_path ?? "",
    cfdi_xml_path: expense?.cfdi_xml_path ?? "",
    paid_by: expense?.paid_by ?? "sibaris",
    paid_by_other: expense?.paid_by_other ?? "",
    payment_method: expense?.payment_method ?? "transferencia",
    project_id: expense?.project_id == null ? "" : String(expense.project_id),
    notes: expense?.notes ?? "",
    receipt_path: expense?.receipt_path ?? "",
  };
}

/** A select value back to the id type of the data: numeric strings become numbers, blank stays blank. */
export function toIdentifier(value: string): string | number {
  return /^\d+$/.test(value) ? Number(value) : value;
}

/** Parses an amount typed by a person; NaN when blank or not a number. */
export function parseAmount(value: string): number {
  return value.trim() === "" ? Number.NaN : Number(value);
}

/**
 * The write payload of a form: only the editable columns (through
 * `toExpenseWriteData`), CFDI files dropped while the expense is not invoiced.
 */
export function buildExpensePayload(
  values: ExpenseFormValues,
): ExpenseWriteData {
  return toExpenseWriteData({
    expense_date: values.expense_date,
    concept: values.concept,
    category_id: toIdentifier(values.category_id),
    supplier: values.supplier,
    amount_mxn: parseAmount(values.amount),
    has_iva: values.has_iva,
    iva_rate: values.iva_rate,
    is_invoiced: values.is_invoiced,
    cfdi_uuid: values.cfdi_uuid,
    cfdi_pdf_path: values.is_invoiced ? values.cfdi_pdf_path : null,
    cfdi_xml_path: values.is_invoiced ? values.cfdi_xml_path : null,
    paid_by: values.paid_by,
    paid_by_other: values.paid_by_other,
    payment_method: values.payment_method,
    project_id: toIdentifier(values.project_id),
    notes: values.notes,
    receipt_path: values.receipt_path,
  });
}

/** Spanish validation messages for a form, one per invalid field. */
export function validateExpenseForm(values: ExpenseFormValues): ExpenseErrors {
  return validateExpense(buildExpensePayload(values));
}
